import { upsertProfileInsights } from './journeyService';
import type { StructuredResume } from '../utils/resumeParser';
import type { ResumeSection } from '../../aiResumeOptimizer/utils/parseResumeSections';

/**
 * Extract skills from LangChain-style structured resume (same as resume upload parsing).
 * Uses Skills and Technical Proficiencies sections for word cloud.
 */
function extractSkillsFromStructured(structured: StructuredResume): string[] {
  const skills: string[] = [];
  const add = (arr: Array<{ text: string }> | undefined) => {
    if (!arr) return;
    arr.forEach((item) => {
      const t = (item?.text ?? '').trim();
      if (t) skills.push(t);
    });
  };
  add(structured?.Skills);
  add(structured?.['Technical Proficiencies']);
  return [...new Set(skills)];
}

function extractSkillsFromSections(sections: ResumeSection[]): string[] {
  const skills: string[] = [];
  const skillSection = sections.find(
    (s) =>
      s.title.toLowerCase().includes('skill') ||
      s.title.toLowerCase().includes('technical') ||
      s.title.toLowerCase().includes('competenc')
  );
  if (skillSection?.content) {
    const parts = skillSection.content.split(/[,;|\n•]/).map((p) => p.trim()).filter(Boolean);
    skills.push(...parts.slice(0, 30));
  }
  return [...new Set(skills)];
}

function inferExperienceLevel(experienceEntries: { duration?: string; highlights?: string[] }[]): string | null {
  if (!experienceEntries?.length) return null;
  let totalYears = 0;
  for (const e of experienceEntries) {
    const d = (e.duration ?? '').match(/(\d+)\s*(year|yr|y)/i);
    if (d) totalYears += parseInt(d[1], 10);
    else if (e.duration) totalYears += 1;
  }
  if (totalYears >= 7) return 'Senior';
  if (totalYears >= 3) return 'Mid-Level';
  if (totalYears >= 1) return 'Junior';
  return 'Entry';
}

function inferIndustryFromContent(content: string): string | null {
  const lower = content.toLowerCase();
  if (lower.includes('software') || lower.includes('backend') || lower.includes('frontend') || lower.includes('full-stack')) return 'Software';
  if (lower.includes('data') || lower.includes('analytics') || lower.includes('machine learning')) return 'Data & Analytics';
  if (lower.includes('product') || lower.includes('pm ')) return 'Product';
  if (lower.includes('finance') || lower.includes('banking')) return 'Finance';
  if (lower.includes('healthcare') || lower.includes('medical')) return 'Healthcare';
  return null;
}

function inferFocusAreas(sections: ResumeSection[]): string[] {
  const focus: string[] = [];
  const short = sections.filter((s) => s.content.length < 200 && s.content.length > 0);
  for (const s of short.slice(0, 2)) {
    const title = s.title.trim();
    if (title && title !== 'Contact' && title !== 'References') focus.push(`Strengthen ${title}`);
  }
  return focus.slice(0, 2);
}

/** Terms to skip in word cloud: contact info, URLs, social, basic details, names */
const WORD_CLOUD_SKIP = new Set([
  'linkedin', 'http', 'https', 'www', 'com', 'org', 'net', 'in', 'co', 'io',
  'gmail', 'yahoo', 'rediffmail', 'outlook', 'hotmail', 'email', 'phone', 'mobile', 'address',
  'name', 'resume', 'cv', 'references', 'contact', 'github', 'portfolio', 'website', 'url',
  'bengaluru', 'bangalore', 'mumbai', 'delhi', 'hyderabad', 'chennai', 'pune', 'india',
  'mr', 'mrs', 'ms', 'dr', 'prof', 'sir', 'dear', 'regards', 'sincerely', 'thank',
  'singh', 'kumar', 'sharma', 'patel', 'rediffmail',
  'dharmendra', 'ahana', 'anana', 'rahul', 'priya', 'amit', 'anil', 'sunita', 'rajesh', 'kavita',
  'suresh', 'deepak', 'vijay', 'sanjay', 'neha', 'pooja', 'ankit', 'rohit', 'manish'
]);

function shouldSkipWordCloudTerm(term: string): boolean {
  if (!term || typeof term !== 'string') return true;
  const t = term.trim().toLowerCase();
  if (t.length < 2) return true;
  if (WORD_CLOUD_SKIP.has(t)) return true;
  if (/@|\.com|\.org|\.net|\.in\b|http|linkedin\.com|github\.com/i.test(t)) return true;
  if (/^\d+$/.test(t)) return true;
  const parts = t.split(/\s+/);
  if (parts.length >= 2 && parts.length <= 3 && parts.every((p) => p.length >= 2 && p.length <= 14 && /^[a-z]+$/.test(p))) {
    if (parts.some((p) => WORD_CLOUD_SKIP.has(p))) return true;
  }
  return false;
}

export function filterWordCloudTerms(terms: string[]): string[] {
  return (terms || []).filter((t) => !shouldSkipWordCloudTerm(t));
}

/** Fallback: when no skills/sections found, extract meaningful terms from raw text for word cloud */
function extractTermsFromRawText(rawText: string): string[] {
  const stop = new Set([
    'the', 'and', 'for', 'with', 'from', 'this', 'that', 'have', 'has', 'had', 'will', 'your', 'you', 'are', 'was', 'were',
    'been', 'being', 'their', 'they', 'them', 'when', 'where', 'which', 'while', 'would', 'could', 'should', 'about', 'into',
    'through', 'during', 'before', 'after', 'above', 'below', 'between', 'under', 'again', 'further', 'then', 'once', 'here',
    'there', 'other', 'more', 'most', 'some', 'such', 'only', 'same', 'than', 'very', 'just', 'also', 'back', 'well', 'even',
    'because', 'both', 'each', 'what', 'how', 'who', 'all', 'any', 'can', 'did', 'get', 'may', 'new', 'now', 'old', 'see', 'way',
    ...WORD_CLOUD_SKIP
  ]);
  const words = rawText
    .replace(/[^\w\s'-]/g, ' ')
    .split(/\s+/)
    .map((w) => w.replace(/^['-]+|['-]+$/g, '').trim().toLowerCase())
    .filter((w) => w.length >= 4 && w.length <= 28 && !stop.has(w) && !/^\d+$/.test(w));
  return filterWordCloudTerms([...new Set(words)].slice(0, 30));
}

function filterInsightsForWordCloud<T extends string[]>(arr: T): T {
  return filterWordCloudTerms(arr) as T;
}

export interface DeriveInsightsFromResumeInput {
  structured?: StructuredResume | null;
  sections?: ResumeSection[] | null;
  rawText?: string | null;
}

/**
 * Derive profile insights from parsed resume and upsert into user_profile_insights.
 * Call after resume parse/upload or when user sets journey.
 */
export async function deriveInsightsFromResume(input: DeriveInsightsFromResumeInput): Promise<void> {
  const { structured, sections, rawText } = input;
  const skills =
    structured ? extractSkillsFromStructured(structured) : sections ? extractSkillsFromSections(sections) : [];
  let experience_level: string | null = null;
  let industry: string | null = null;
  const focus_areas: string[] = [];

  if (structured?.Experience?.length) {
    experience_level = inferExperienceLevel(structured.Experience);
    const firstExp = structured.Experience[0];
    const content = [firstExp.position, firstExp.company, (firstExp.highlights ?? []).join(' ')].join(' ');
    industry = inferIndustryFromContent(content);
  }
  if (sections?.length && !industry && rawText) {
    industry = inferIndustryFromContent(rawText.slice(0, 2000));
  }
  if (sections?.length) {
    focus_areas.push(...inferFocusAreas(sections));
  }

  const technical_strengths = skills.length ? filterInsightsForWordCloud(skills.slice(0, 5)) : [];
  let strengths = technical_strengths.length ? technical_strengths : (skills.length ? filterInsightsForWordCloud(skills.slice(0, 8)) : []);
  let focus_areasFinal = focus_areas.length ? filterInsightsForWordCloud(focus_areas) : (technical_strengths.length ? ['Technical skills'] : []);
  if (strengths.length === 0 && rawText && rawText.trim().length > 50) {
    strengths = extractTermsFromRawText(rawText.trim());
  }
  if (focus_areasFinal.length === 0 && strengths.length > 0) {
    focus_areasFinal = ['Highlight key skills', 'Tailor to each job'];
  }
  const weaknesses = focus_areas.length ? filterInsightsForWordCloud(focus_areas) : (skills.length ? [] : []);
  await upsertProfileInsights({
    skills: filterInsightsForWordCloud(skills),
    strengths,
    weaknesses,
    focus_areas: focus_areasFinal,
    technical_strengths,
    experience_level,
    industry,
    last_parsed_at: new Date().toISOString(),
  });
}
